/**
 * NewClientModal — shared "Create a new client" modal.
 *
 * Used by:
 *   • Clients list page (top-right button)
 *   • Invoice Builder client picker (inline create)
 *
 * Renders a fixed-position overlay with the form. `onCreated(client)` fires
 * with the freshly created/saved record so the caller can wire it in.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { X, Plus } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const empty = () => ({
  name: '', business_name: '', email: '', phone: '',
  billing_address: '', shipping_address: '',
  country: '', state: '', zip: '', website: '',
  tags: [], status: 'active',
});

export default function NewClientModal({ open, onClose, onCreated, prefill }) {
  const [state, setState] = useState({ ...empty(), ...(prefill || {}) });
  const [saving, setSaving] = useState(false);
  const [tagDraft, setTagDraft] = useState('');

  React.useEffect(() => {
    if (open) setState({ ...empty(), ...(prefill || {}) });
  }, [open, prefill]);

  if (!open) return null;
  const upd = (k, v) => setState(s => ({ ...s, [k]: v }));

  const addTag = () => {
    const t = tagDraft.trim().toLowerCase();
    if (!t) return;
    if (state.tags.includes(t)) { setTagDraft(''); return; }
    setState(s => ({ ...s, tags: [...s.tags, t] }));
    setTagDraft('');
  };

  const save = async () => {
    if (!state.name && !state.business_name) {
      toast.error('Enter a name or business name'); return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/builder/crm/clients`, state, { headers: auth() });
      toast.success('Client added');
      onCreated?.(data);
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save client');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="new-client-modal">
      <div className="bg-[#11112a] border border-white/10 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-[#11112a]/95 backdrop-blur border-b border-white/5 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-violet-300/80 mb-0.5">New client</div>
            <h2 className="text-lg font-semibold text-white">Add a client to your roster</h2>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white" data-testid="new-client-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-5">
          <Row>
            <Field label="Contact name" v={state.name} on={v => upd('name', v)} testid="nc-name" placeholder="Jane Doe" />
            <Field label="Business name" v={state.business_name} on={v => upd('business_name', v)} testid="nc-business" placeholder="Acme Studio" />
          </Row>
          <Row>
            <Field label="Email" v={state.email} on={v => upd('email', v)} type="email" testid="nc-email" placeholder="jane@acme.com" />
            <Field label="Phone" v={state.phone} on={v => upd('phone', v)} testid="nc-phone" placeholder="+1 555 1234" />
          </Row>
          <Field label="Website" v={state.website} on={v => upd('website', v)} testid="nc-website" placeholder="https://acme.com" full />
          <Field label="Billing address" v={state.billing_address} on={v => upd('billing_address', v)} testid="nc-billing" placeholder="Street, city" full multiline />
          <Field label="Shipping address (if different)" v={state.shipping_address} on={v => upd('shipping_address', v)} testid="nc-shipping" full multiline />
          <Row triple>
            <Field label="Country" v={state.country} on={v => upd('country', v)} testid="nc-country" />
            <Field label="State / Region" v={state.state} on={v => upd('state', v)} testid="nc-state" />
            <Field label="Zip / Postal" v={state.zip} on={v => upd('zip', v)} testid="nc-zip" />
          </Row>

          <div>
            <div className="text-xs text-white/40 mb-1.5">Tags</div>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {state.tags.map(t => (
                <span key={t} className="px-2 py-0.5 rounded bg-violet-500/15 border border-violet-400/30 text-violet-200 text-xs inline-flex items-center gap-1.5" data-testid={`nc-tag-${t}`}>
                  {t}
                  <button onClick={() => setState(s => ({ ...s, tags: s.tags.filter(x => x !== t) }))} className="text-violet-300/60 hover:text-white"><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input value={tagDraft} onChange={e => setTagDraft(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }} placeholder="retainer, vip…" data-testid="nc-tag-input"
                className="flex-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-white/25 outline-none focus:border-violet-400" />
              <button onClick={addTag} data-testid="nc-tag-add" className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-white text-sm inline-flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> Add</button>
            </div>
          </div>

          <div>
            <div className="text-xs text-white/40 mb-1.5">Status</div>
            <div className="flex gap-2">
              {['active', 'lead', 'archived'].map(s => (
                <button key={s} onClick={() => upd('status', s)} data-testid={`nc-status-${s}`}
                  className={`px-3 py-1.5 rounded-lg text-xs capitalize ${state.status === s ? 'bg-violet-500 text-white' : 'bg-white/5 text-white/60 hover:text-white border border-white/10'}`}>{s}</button>
              ))}
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-[#11112a]/95 backdrop-blur border-t border-white/5 px-6 py-4 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/80 text-sm" data-testid="new-client-cancel">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="new-client-save"
            className="px-4 py-2 rounded-lg bg-violet-500 hover:bg-violet-400 text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> {saving ? 'Saving…' : 'Save client'}
          </button>
        </div>
      </div>
    </div>
  );
}

const Row = ({ children, triple }) => (
  <div className={`grid gap-3 ${triple ? 'grid-cols-3' : 'grid-cols-2'}`}>{children}</div>
);
const Field = ({ label, v, on, testid, type = 'text', placeholder, full, multiline }) => (
  <div className={full ? 'col-span-2' : ''}>
    <div className="text-xs text-white/40 mb-1">{label}</div>
    {multiline ? (
      <textarea value={v ?? ''} onChange={e => on(e.target.value)} placeholder={placeholder} data-testid={testid} rows={2}
        className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-white/25 outline-none focus:border-violet-400 resize-y" />
    ) : (
      <input type={type} value={v ?? ''} onChange={e => on(e.target.value)} placeholder={placeholder} data-testid={testid}
        className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-white/25 outline-none focus:border-violet-400" />
    )}
  </div>
);
